import { useEffect, useState, useMemo } from "react";
import { fetchAdminCommissions } from "../../../api/finance";
import "../../../styles/AdminPanel/AdminCommissions.css";
import dayjs from "dayjs";
import jalaliday from "jalaliday";

dayjs.extend(jalaliday);

const PAGE_SIZE = 10;

export default function AdminCommissions() {
  const [items, setItems] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [marketerPhone, setMarketerPhone] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAdminCommissions().then((res) => setItems(res.data));
  }, []);

  const toJalali = (date) => {
    if (!date) return "-";
    return dayjs(date).calendar("jalali").format("YYYY/MM/DD");
  };

  const getJalaliYear = (date) => dayjs(date).calendar("jalali").format("YYYY");
  const getJalaliMonth = (date) => dayjs(date).calendar("jalali").format("MM");

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (item.owner_phone === item.marketer_phone) return false;

      const y = getJalaliYear(item.plan_start_date);
      const m = getJalaliMonth(item.plan_start_date);

      if (yearFilter !== "all" && y !== yearFilter) return false;
      if (monthFilter !== "all" && m !== monthFilter) return false;
      if (marketerPhone && !item.marketer_phone?.includes(marketerPhone))
        return false;

      return true;
    });
  }, [items, yearFilter, monthFilter, marketerPhone]);

  // با تغییر فیلترها، برگرد به صفحه‌ی اول
  useEffect(() => {
    setPage(1);
  }, [yearFilter, monthFilter, marketerPhone]);

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const pagedItems = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, pageSafe]);

  const totalCommission = filteredItems.reduce(
    (sum, i) => sum + Number(i.commission_amount || 0),
    0,
  );

  const years = [
    ...new Set(items.map((i) => getJalaliYear(i.plan_start_date))),
  ];

  return (
    <div className="page">
      <h2>پورسانت‌های فروش</h2>

      <div className="top-row">
        <div className="total-commission">
          جمع کل پورسانت: {totalCommission.toLocaleString()} تومان
        </div>

        <div className="commission-filters">
          <input
            className="search-input"
            placeholder="جستجو با شماره مارکتر"
            value={marketerPhone}
            onChange={(e) => setMarketerPhone(e.target.value)}
          />

          <select
            className="search-input"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value="all">همه سال‌ها</option>
            {years.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>

          <select
            className="search-input"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            <option value="all">همه ماه‌ها</option>
            <option value="01">فروردین</option>
            <option value="02">اردیبهشت</option>
            <option value="03">خرداد</option>
            <option value="04">تیر</option>
            <option value="05">مرداد</option>
            <option value="06">شهریور</option>
            <option value="07">مهر</option>
            <option value="08">آبان</option>
            <option value="09">آذر</option>
            <option value="10">دی</option>
            <option value="11">بهمن</option>
            <option value="12">اسفند</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <th>شماره اینویس</th>
              <th>نام فروشگاه</th>
              <th>شماره مالک</th>
              <th>نام مارکتر</th>
              <th>شماره مارکتر</th>
              <th>تاریخ شروع</th>
              <th>قیمت پلن</th>
              <th>پورسانت</th>
              <th>جزئیات</th>
            </tr>
          </thead>

          <tbody>
            {pagedItems.map((x, i) => (
              <tr key={x.id ?? i}>
                <td>{(pageSafe - 1) * PAGE_SIZE + i + 1}</td>
                <td>{x.invoice_number}</td>
                <td>{x.store_name}</td>
                <td className="phone-cell">{x.owner_phone}</td>
                <td>{x.marketer_name}</td>
                <td className="phone-cell">{x.marketer_phone}</td>
                <td>{toJalali(x.plan_start_date)}</td>
                <td>{Number(x.plan_price).toLocaleString()} تومان</td>
                <td>{Number(x.commission_amount).toLocaleString()} تومان</td>
                <td>
                  <button
                    className="details-btn"
                    onClick={() => setSelectedItem(x)}
                    title="نمایش جزئیات"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}

            {filteredItems.length === 0 && (
              <tr>
                <td
                  colSpan="10"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  داده‌ای یافت نشد
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} مورد — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>جزئیات پورسانت</h3>

            <div className="modal-section">
              <span className="modal-section-title">فروشگاه</span>
              <div className="modal-row">
                <span className="modal-label">شماره اینویس</span>
                <span className="modal-value">
                  {selectedItem.invoice_number}
                </span>
              </div>
              <div className="modal-row">
                <span className="modal-label">نام فروشگاه</span>
                <span className="modal-value">{selectedItem.store_name}</span>
              </div>
              <div className="modal-row">
                <span className="modal-label">شماره مالک</span>
                <span className="modal-value phone-cell">
                  {selectedItem.owner_phone}
                </span>
              </div>
            </div>

            <div className="modal-section">
              <span className="modal-section-title">مارکتر</span>
              <div className="modal-row">
                <span className="modal-label">نام مارکتر</span>
                <span className="modal-value">
                  {selectedItem.marketer_name}
                </span>
              </div>
              <div className="modal-row">
                <span className="modal-label">شماره مارکتر</span>
                <span className="modal-value phone-cell">
                  {selectedItem.marketer_phone}
                </span>
              </div>
            </div>

            <div className="modal-section">
              <span className="modal-section-title">پلن</span>
              <div className="modal-row">
                <span className="modal-label">تاریخ شروع</span>
                <span className="modal-value">
                  {toJalali(selectedItem.plan_start_date)}
                </span>
              </div>
              <div className="modal-row">
                <span className="modal-label">قیمت پلن</span>
                <span className="modal-value">
                  {Number(selectedItem.plan_price).toLocaleString()} تومان
                </span>
              </div>
            </div>

            <div className="modal-total">
              <span>پورسانت</span>
              <span>
                {Number(selectedItem.commission_amount).toLocaleString()} تومان
              </span>
            </div>

            <button
              className="detail-btn"
              onClick={() => setSelectedItem(null)}
            >
              بستن
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
